import { Text, ToastBar } from '@shopuptech/expo-shopup-ui';
import { helpers, types, validators } from '@shopuptech/ui-core';
import classNames from 'classnames/bind';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { navLogo, panelName, showTopbar } from './config';
import useRequireLogin from './hooks/requireLoginHook';
import {
  CheckLoginState,
  ItemsEntity,
  PermissionScopeState,
} from './mainAction.types';
import {
  clearPermissionsAction,
  clearUserInfo,
  logoutAction,
} from './mainActions';
import {
  GRANT_ACCESS_PATH,
  LOGIN_PATH,
  NO_PERMISSION_PATH,
  REQUEST_ACCESS_PATH,
} from './modules/mainhome/constants';
import ForgotPassword from './modules/mainhome/LoginScreen/ForgotPassword';
import LoginMain from './modules/mainhome/LoginScreen/Loginmain';
import NoPermissionScreen from './modules/NoPermissionScreen/NoPermissionScreen';
import NavBar from './navbar/Navbar';
import styles from './navigation.module.css';
import { navigateOveride } from './override';
import env from './variables';
import GrantAccess from './modules/mainhome/LoginScreen/GrantAccessScreen';
import RequestAccessScreen from './modules/NoPermissionScreen/RequestAccessScreen';
import { useUserLogin } from './hooks/useUserLogin';

const cx = classNames.bind(styles);
const { getModuleNameFromRoutes, initialiseExternalLibs } = helpers;
const { isEmpty } = validators;

type Routes = {
  path: string;
  name: string;
  component: any;
  routes?: Routes[];
};

type Props = {
  initialRoute: string;
  combinedRoutes: Routes[];
};

export type MainRootState = {
  main: {
    checkLogin: CheckLoginState;
    scopes: PermissionScopeState;
  };
};

const RouteWithSubRoutes = (route: Routes) => {
  return (
    <Route
      path={route.path}
      render={(props) => <route.component {...props} routes={route.routes} />}
    />
  );
};

const getAllowedRoutes = (routes: Routes[], scopes: ItemsEntity[]) => {
  const panels = scopes.map((scope) => {
    const panel = scope.id.split(':');
    return panel[0];
  });

  return routes.filter((route) => {
    if (typeof route.path !== 'string') {
      return false;
    }

    const panelNameFinal = route.path
      .split('/')[1]
      .split('?')[0]
      .replace('-', '');
    return panels.includes(panelNameFinal);
  });
};

const handleDashboradClick = () =>
  (window.location.href = window.location.origin);

const Navigation = ({ combinedRoutes }: Props) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch: types.Dispatch<any> = useDispatch();
  const [errorMessage, setErrorMessage] = useState('');
  const [mainRoutes, setMainRoutes] = useState<Routes[]>([]);
  const [init, setInit] = useState(false);

  useRequireLogin(false);

  const {
    permissionList,
    user,
    isLoggedin,
    id,
    email,
    phone,
    username,
    isLoading,
  } = useUserLogin();

  useEffect(() => {
    initialiseExternalLibs({
      environment: panelName + ':' + env.appEnv,
      sentryID: env.sentryID,
      enableErrorTracing: env.enableSentry,
      licenseKey: env.licenseKey,
      applicationID: env.applicationID,
      panelName: panelName,
      configureScope: {
        id: `${id}`,
        username,
        email,
        phone,
      },
    });
  }, [id]);

  const moduleName = getModuleNameFromRoutes(location);
  useEffect(() => {
    dispatch({ type: '@@INIT_APP', meta: { moduleName } });
    setInit(true);
  }, [moduleName, dispatch]);

  useEffect(() => {
    if (location.pathname === LOGIN_PATH) {
      setMainRoutes([]);
    } else {
      const filteredRoutes = getAllowedRoutes(combinedRoutes, permissionList);
      setMainRoutes(filteredRoutes);

      const excludedPaths = [REQUEST_ACCESS_PATH, GRANT_ACCESS_PATH];

      const isExcludedPath = excludedPaths.some(
        (path) =>
          location.pathname === path ||
          location.pathname.startsWith(path + '/'),
      );

      if (
        filteredRoutes.length === 1 &&
        env.appEnv !== 'development' &&
        !isExcludedPath
      ) {
        const { path } = filteredRoutes[0];
        const formattedPath: string = `${path}/`;
        const isOverride = navigateOveride(formattedPath, true);
        if (!isOverride) {
          window.location.href = formattedPath;
        }
      }
    }
  }, [location.pathname, combinedRoutes, permissionList]);

  useEffect(() => {
    if (
      location.pathname !== NO_PERMISSION_PATH &&
      location.pathname !== REQUEST_ACCESS_PATH &&
      isLoggedin &&
      !isLoading &&
      permissionList.length === 0
    ) {
      history.push(NO_PERMISSION_PATH);
    }
  }, [location.pathname, isLoggedin, isLoading, permissionList]);

  const handleNavigate = (path: string) => {
    setInit(false);
    const isOverride = navigateOveride(path, true);
    if (!isOverride) {
      window.location.href = path;
    }
  };

  const handleLogout = () => {
    dispatch(logoutAction())
      .then(() => {
        dispatch(clearPermissionsAction());
        dispatch(clearUserInfo());
        if (
          location.pathname === '/dashboard' ||
          location.pathname === NO_PERMISSION_PATH ||
          location.pathname === REQUEST_ACCESS_PATH
        ) {
          history.push(LOGIN_PATH);
        } else if (env.appEnv !== 'development') {
          window.location.href = `${LOGIN_PATH}?ref=` + location.pathname;
        }
      })
      .catch((error) => {
        const { error: { message = '' } = {} } = error;
        setErrorMessage(message);
      });
  };

  const handleClearErrorMessage = () => setErrorMessage('');
  return (
    <NavBar
      logoClick={handleDashboradClick}
      user={user}
      routes={mainRoutes}
      navigate={handleNavigate}
      handleLogout={handleLogout}
      logo={navLogo}
      collapsable={false}
      showTopBar={showTopbar}>
      <div
        className={cx('navChildren')}
        style={{ maxHeight: window.innerHeight }}>
        {!isEmpty(errorMessage) && (
          <ToastBar type="error" callback={handleClearErrorMessage}>
            <Text testID="error-message-text" color="error">
              {errorMessage}
            </Text>
          </ToastBar>
        )}
        {init && (
          <Switch>
            <Route path={LOGIN_PATH} exact component={LoginMain} />
            <Route path="/forgot-password/:token" component={ForgotPassword} />
            <Route path={NO_PERMISSION_PATH} component={NoPermissionScreen} />
            <Route path={REQUEST_ACCESS_PATH} component={RequestAccessScreen} />
            <Route
              path={`${GRANT_ACCESS_PATH}/:entityId`}
              component={GrantAccess}
            />
            {mainRoutes.map((route) => (
              <RouteWithSubRoutes key={route.path} {...route} />
            ))}
          </Switch>
        )}
      </div>
    </NavBar>
  );
};

export default Navigation;
